/*
 * Lemonade Training Oy copy rights all right reserved.
 * Image Variant Serving Contract
 */

/**
 * Cross-platform contract for serving resized image variants.
 *
 * A backend resize pipeline generates small WebP copies of every uploaded
 * image into a dedicated `_resized` subfolder next to the original. Web and
 * mobile resolvers derive variant paths and pick a serving branch from the
 * exact values in this module, so every value here is FROZEN: changing one
 * changes where variants live (or how they are served) for every platform at
 * once, and must be a deliberate, coordinated decision.
 *
 * Two behaviors of the pipeline that this contract encodes:
 *
 * 1. Variant naming depends on the ORIGINAL's filename extension:
 *    - Extension-less originals (`ProfilePic`, `logo`, uid-keyed cover
 *      arts …) get BARE variant names — no format suffix:
 *      `users/{uid}/_resized/ProfilePic_200x200`. The bytes and
 *      content-type are WebP, the name is bare.
 *    - Originals whose filename carries a pipeline-recognized raster
 *      extension (cover arts keyed by the uploaded file's name, e.g.
 *      `photo.jpg`) get the extension REPLACED with the variant format:
 *      `users/{uid}/CoverArts/_resized/photo_200x200.webp`.
 * 2. The public/tokened split is per DIRECTORY FAMILY, not per file. Profile
 *    pictures and user landing banners share the `users/{uid}` folder, so
 *    both serve via the tokened branch; landing banners ride it by necessity
 *    (folder granularity), not privacy intent.
 */

/** Bounding-box sizes generated for every image (aspect ratio preserved). */
export const IMAGE_VARIANT_SIZES = ["200x200", "512x512", "1280x1280"] as const;

export type ImageVariantSize = (typeof IMAGE_VARIANT_SIZES)[number];

/** Dedicated subfolder (inside each original's directory) holding variants. */
export const RESIZED_DIR_NAME = "_resized";

/** Variant bytes/content-type (filename carries a suffix only when the
 * original had a recognized extension — see the module header). */
export const VARIANT_CONTENT_TYPE = "image/webp";

/**
 * Query-param name both platforms append to a resolved variant URL
 * (`?v={originalToken}`). The original's download token rotates on every
 * re-upload, so keying the URL on it busts long-lived edge/browser/disk
 * caches and keys any in-memory result cache: a re-upload is a new URL, and a
 * stale image can never be served from cache once the token has rotated.
 */
export const VARIANT_CACHE_BUST_PARAM = "v";

/**
 * Filename extensions the resize pipeline recognizes on originals. A variant
 * of such an original is named `{stem}_{size}.webp`; originals with any
 * other (or no) extension keep bare `{basename}_{size}` variant names.
 */
export const VARIANT_SOURCE_EXTENSIONS = [
  ".avif",
  ".gif",
  ".jfif",
  ".jpeg",
  ".jpg",
  ".png",
  ".tif",
  ".tiff",
  ".webp",
] as const;

/**
 * Image path prefixes whose variants are served via token-less public URLs
 * (`https://storage.googleapis.com/{bucket}/{path}`): box branding assets and
 * customer-facing product cover arts.
 */
export const PUBLIC_IMAGE_PREFIXES = [
  "/boxes/*/banner",
  "/boxes/*/coverArt",
  "/boxes/*/landingPageBanner",
  "/boxes/*/logo",
  "/users/*/CoverArts/*",
] as const;

/**
 * Image path prefixes whose variants are served via tokened download URLs
 * (revocable, never carrying a permanent public object ACL): profile
 * pictures, plus user landing banners (same folder as profile pictures —
 * inseparable at the pipeline's directory granularity).
 */
export const PRIVATE_IMAGE_PREFIXES = [
  "/users/*/LandingPageBanner",
  "/users/*/ProfilePic",
] as const;

/** Every storage path prefix that holds resizable uploaded images. */
export const ALL_IMAGE_PREFIXES = [
  ...PUBLIC_IMAGE_PREFIXES,
  ...PRIVATE_IMAGE_PREFIXES,
].sort() as readonly string[];

/** Serving branch for a resolved variant URL. */
export const IMAGE_SERVING_BRANCH = {
  PUBLIC: "public",
  TOKENED: "tokened",
} as const;

export type ImageServingBranch =
  (typeof IMAGE_SERVING_BRANCH)[keyof typeof IMAGE_SERVING_BRANCH];

/**
 * Tests an object path against one contract prefix (glob form, `*` = exactly
 * one path segment).
 */
const matchesPrefix = (segments: string[], prefix: string): boolean => {
  const prefixSegments = prefix.replace(/^\//, "").split("/");
  if (segments.length !== prefixSegments.length) return false;
  return prefixSegments.every(
    (prefixSegment, index) =>
      prefixSegment === "*" || prefixSegment === segments[index],
  );
};

/**
 * Builds the storage object path of a variant for an original's object path,
 * following the pipeline's naming rules (see the module header):
 * - `users/u1/CoverArts/p1` + `"512x512"` → `users/u1/CoverArts/_resized/p1_512x512`
 * - `users/u1/CoverArts/photo.jpg` + `"512x512"` → `users/u1/CoverArts/_resized/photo_512x512.webp`
 */
export const buildVariantPath = (
  objectPath: string,
  size: ImageVariantSize,
): string => {
  const segments = objectPath.replace(/^\//, "").split("/");
  const basename = segments.pop() ?? "";
  const dotIndex = basename.lastIndexOf(".");
  const extension = dotIndex > 0 ? basename.slice(dotIndex).toLowerCase() : "";
  const variantName = (VARIANT_SOURCE_EXTENSIONS as readonly string[]).includes(
    extension,
  )
    ? `${basename.slice(0, dotIndex)}_${size}.${VARIANT_CONTENT_TYPE.split("/")[1]}`
    : `${basename}_${size}`;
  return [...segments, RESIZED_DIR_NAME, variantName].join("/");
};

/**
 * Returns which serving branch an original's object path belongs to, or null
 * when the path is not a contract image path (callers must fall back to the
 * original URL for null — the resolver must never break an image).
 */
export const servingBranchForPath = (
  objectPath: string,
): ImageServingBranch | null => {
  const segments = objectPath.replace(/^\//, "").split("/");
  if (PUBLIC_IMAGE_PREFIXES.some((prefix) => matchesPrefix(segments, prefix))) {
    return IMAGE_SERVING_BRANCH.PUBLIC;
  }
  if (
    PRIVATE_IMAGE_PREFIXES.some((prefix) => matchesPrefix(segments, prefix))
  ) {
    return IMAGE_SERVING_BRANCH.TOKENED;
  }
  return null;
};

/**
 * A stored image URL decomposed into the pieces the variant resolver needs.
 * `objectPath` is the percent-decoded storage object path (no leading slash);
 * `token` is the original's download token when the URL carried one (it
 * rotates on re-upload — see {@link VARIANT_CACHE_BUST_PARAM}); `emulatorOrigin`
 * is set only for Storage-emulator URLs, so variant URLs for emulator-stored
 * originals can be kept on that same origin instead of an absolute GCS URL
 * that would bypass the emulator.
 */
export interface ParsedStorageUrl {
  bucket: string;
  objectPath: string;
  token: string | null;
  emulatorOrigin: string | null;
}

/**
 * Rejects object paths with empty or dot segments. The contract's `*` wildcard
 * (see {@link servingBranchForPath}) would otherwise match `.`/`..`, letting a
 * crafted stored URL address sibling objects — this is the path-traversal
 * defense and every caller depends on it.
 */
const hasSaneSegments = (objectPath: string): boolean =>
  objectPath
    .split("/")
    .every((segment) => segment !== "" && segment !== "." && segment !== "..");

/**
 * Parses the two stored/served URL shapes into a {@link ParsedStorageUrl}, or
 * returns null for anything else (blob: previews, data: URIs, external images,
 * malformed URLs) so a caller can fall through to the original URL untouched.
 *
 * Recognized shapes:
 * - Firebase download channel — `firebasestorage.googleapis.com`
 *   `/v0/b/{bucket}/o/{percent-encoded objectPath}` with a `token` param.
 * - Direct GCS channel — `storage.googleapis.com/{bucket}/{path}` with a
 *   {@link VARIANT_CACHE_BUST_PARAM} (`v`) param carrying the token.
 *
 * Guards preserved from the original resolver: only http(s) protocols; strict
 * path-shape regexes; `decodeURIComponent` wrapped so malformed percent-
 * encoding yields null; and the {@link hasSaneSegments} traversal defense.
 *
 * Platform seam: the Storage EMULATOR serves the same `/v0/b/.../o/...` shape
 * on `localhost`/`127.0.0.1`. Because this package cannot read `process.env` or
 * `window`, those hosts are accepted ONLY when `options.allowEmulatorHosts` is
 * true — each platform passes its own emulator/dev detection. Default is false,
 * so a stored localhost URL in a production build stays passthrough (trusted
 * code never launders an attacker-influenceable origin, even a nil-harm one).
 */
export const parseStorageUrl = (
  url: string,
  options?: { allowEmulatorHosts?: boolean },
): ParsedStorageUrl | null => {
  const allowEmulatorHosts = options?.allowEmulatorHosts === true;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;

  const isEmulatorHost =
    parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1";
  const isFirebaseChannelHost =
    parsed.hostname === "firebasestorage.googleapis.com" ||
    (isEmulatorHost &&
      parsed.pathname.startsWith("/v0/b/") &&
      allowEmulatorHosts);

  if (isFirebaseChannelHost) {
    // /v0/b/{bucket}/o/{percent-encoded object path}
    const match = parsed.pathname.match(/^\/v0\/b\/([^/]+)\/o\/(.+)$/);
    if (!match) return null;
    try {
      const objectPath = decodeURIComponent(match[2]);
      if (!hasSaneSegments(objectPath)) return null;
      return {
        bucket: match[1],
        objectPath,
        token: parsed.searchParams.get("token"),
        emulatorOrigin: isEmulatorHost ? parsed.origin : null,
      };
    } catch {
      return null;
    }
  }

  if (parsed.hostname === "storage.googleapis.com") {
    // /{bucket}/{object path with literal slashes}
    const segments = parsed.pathname.replace(/^\//, "").split("/");
    if (segments.length < 2) return null;
    const bucket = segments.shift() as string;
    try {
      const objectPath = segments
        .map((segment) => decodeURIComponent(segment))
        .join("/");
      if (!hasSaneSegments(objectPath)) return null;
      return {
        bucket,
        objectPath,
        token: parsed.searchParams.get(VARIANT_CACHE_BUST_PARAM),
        emulatorOrigin: null,
      };
    } catch {
      return null;
    }
  }

  return null;
};

/**
 * Builds a PUBLIC-branch variant URL (a pure string, no network round-trip)
 * for a variant object path — the counterpart of {@link parseStorageUrl} for
 * paths whose {@link servingBranchForPath} is `public`.
 *
 * - Production form:
 *   `https://storage.googleapis.com/{bucket}/{per-segment-encoded variantPath}`,
 *   plus `?{VARIANT_CACHE_BUST_PARAM}={encoded token}` when a token is present.
 * - Emulator form (when `emulatorOrigin` is set):
 *   `{emulatorOrigin}/v0/b/{bucket}/o/{encodeURIComponent(variantPath)}?alt=media`,
 *   plus `&{VARIANT_CACHE_BUST_PARAM}={encoded token}` when a token is present.
 *   Emulator originals must keep their variant URLs on the emulator origin — an
 *   absolute GCS URL would silently bypass the emulator.
 */
export const buildPublicVariantUrl = (args: {
  bucket: string;
  variantPath: string;
  token: string | null;
  emulatorOrigin?: string | null;
}): string => {
  const { bucket, variantPath, token, emulatorOrigin } = args;

  if (emulatorOrigin) {
    const cacheBust = token
      ? `&${VARIANT_CACHE_BUST_PARAM}=${encodeURIComponent(token)}`
      : "";
    return `${emulatorOrigin}/v0/b/${bucket}/o/${encodeURIComponent(
      variantPath,
    )}?alt=media${cacheBust}`;
  }

  const encodedPath = variantPath
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  const cacheBust = token
    ? `?${VARIANT_CACHE_BUST_PARAM}=${encodeURIComponent(token)}`
    : "";
  return `https://storage.googleapis.com/${bucket}/${encodedPath}${cacheBust}`;
};
